import React, { ChangeEvent } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {CustomFieldProps} from 'components/core/Form';
import {Selector, TextField, Timespan} from 'components/core';
import {InputAdornment, SvgIcon} from '@material-ui/core';


const useStyles = makeStyles((theme) => ({
    datesContainer:
    {
      display: "flex",
      flexDirection: "row",
      alignContent: "space-between"
    },
    dateField: {
        flexGrow: 1,
        padding: "5px 0"
    },
    separator: {
        flexGrow: 0,
        flexBasis: 30
    },
    svg: {
        color: theme.palette.text.primary,
        width: 20,
        height: 20,
        margin: 6
    }
  }), {name: "TaskDatePickerField"});

const selectorItems = [
    {
        value: "between-dates",
        content: "Entre dos fechas"
    },
    {
        value: "before",
        content: "Antes del"
    },
    {
        value: "after",
        content: "A partir del"
    },
    {
        value: "exact-day",
        content: "El dia exacto"
    }
];

export default function TaskDatePickerField({onChange, value} : CustomFieldProps)
{
    const classes = useStyles();
    
    const [internalValue, setInternalValue] = React.useState(value ? value : { minStartDate: null, maxStartDate: new Date().toISOString() });
    
    const selectorVal = (internalValue.minStartDate && internalValue.maxStartDate ? (internalValue.minStartDate === internalValue.maxStartDate ? 'exact-day' : 'between-dates') : (internalValue.minStartDate ? 'after' : 'before'));

    React.useEffect(() => {
        setInternalValue(value ? value : { minStartDate: null, maxStartDate: new Date().toISOString() });
    }, [value]);

    function handleSelectorChange(evt : React.ChangeEvent<{ value: string }>)
    {
        let updatedValue = {...internalValue};

        switch(evt.target.value)
        {
            case 'between-dates':
                
                // if they both exist and are different, it's just right, skip
                if(updatedValue.minStartDate && updatedValue.maxStartDate && updatedValue.minStartDate !== updatedValue.maxStartDate) break;                
                // if there is a minStartDate, use it as a base, set max to min + 1day
                else if(updatedValue.minStartDate) updatedValue.maxStartDate = new Date(new Date(updatedValue.minStartDate).getDate()+1).toISOString();
                // if there is no min, use max as a base, set min to max - 1day
                else
                {
                    let date = new Date(updatedValue.maxStartDate);
                    date.setDate(date.getDate() - 1);
                    updatedValue.minStartDate = date.toISOString();
                }
                
                break;
            case 'before':
                // if a max is already set, just null the min
                if(updatedValue.maxStartDate) updatedValue.minStartDate = null;
                // if there is a min, use it as the max, and null the min
                else if(updatedValue.minStartDate)
                {
                    updatedValue.maxStartDate = updatedValue.minStartDate;
                    updatedValue.minStartDate = null;
                }
                // if no date is set, use "tomorrow" as default
                else updatedValue.maxStartDate = new Date(new Date().getDate()+1).toISOString();
                break;
            case 'after':
                // if a min is already set, just null the max
                if(updatedValue.minStartDate) updatedValue.maxStartDate = null;
                // if there is a max, use it as the min, and null the max
                else if(updatedValue.maxStartDate)
                {
                    updatedValue.minStartDate = updatedValue.maxStartDate;
                    updatedValue.maxStartDate = null;
                }
                // if no date is set, use "today" as default
                else updatedValue.minStartDate = new Date().toISOString();
                break;
            case 'exact-day':
                if(updatedValue.minStartDate) updatedValue.maxStartDate = updatedValue.minStartDate;
                else if(updatedValue.maxStartDate) updatedValue.minStartDate = updatedValue.maxStartDate;
                else
                {
                    updatedValue.minStartDate = new Date(new Date().getDate()+1).toISOString();
                    updatedValue.maxStartDate = updatedValue.minStartDate;
                }
                break;
        }
        setInternalValue(updatedValue);
        onChange(updatedValue);
    }

    function handleFieldChange(field : 'min' | 'max' | 'unique', newValue: string)
    {
        let updatedValue = {...internalValue};

        switch(field)
        {
            case 'min':
                updatedValue.minStartDate = newValue;
                break;
            case 'max':
                updatedValue.maxStartDate = newValue;
                break;
            case 'unique':
                updatedValue.minStartDate = newValue;
                updatedValue.maxStartDate = newValue;
                break;
        }

        setInternalValue(updatedValue);
        onChange(updatedValue);
    }

    let datesBlock = <></>;
    let minDate = <div className={classes.dateField}><TextField
        type="date"
        onChange={(evt: React.ChangeEvent<{ value: string}>) => handleFieldChange('min', evt.target.value)}
        value={internalValue.minStartDate !== "" ? new Date(internalValue.minStartDate).toISOString().slice(0,10) : ""}
        startAdornment={
            <InputAdornment position="start">
            <SvgIcon className={classes.svg}>
                <path fill="currentColor" xmlns="http://www.w3.org/2000/svg" d="M2 14H4V17H9V14L13 18L9 22V19H4V22H2V14ZM19 19V8.00005H5V12H3V5.00005C3 3.89005 3.89 3.00005 5 3.00005H6V0.998047H8V3.00005H16V0.998047H18V3.00005H19C19.5304 3.00005 20.0391 3.21076 20.4142 3.58583C20.7893 3.96091 21 4.46961 21 5.00005V19C21 20.1 20.1 21 19 21H12.83L14.83 19H19Z"/>
                </SvgIcon>
            </InputAdornment>
        }
        required
    /></div>
    
    let maxDate = <div className={classes.dateField}><TextField
        type="date"
        onChange={(evt: React.ChangeEvent<{ value: string}>) => handleFieldChange('max', evt.target.value)}
        value={internalValue.maxStartDate !== "" ? new Date(internalValue.maxStartDate).toISOString().slice(0,10) : ""}
        startAdornment={
            <InputAdornment position="start">
            <SvgIcon className={classes.svg}>
                <path fill="currentColor" xmlns="http://www.w3.org/2000/svg" d="M22 14V22H20V18L16 22V19H11V17H16V14L20 18V14H22ZM5 19H9V21H5C3.9 21 3 20.1 3 19V5.00005C3 4.46961 3.21071 3.96091 3.58579 3.58583C3.96086 3.21076 4.46957 3.00005 5 3.00005H6V0.998047H8V3.00005H16V0.998047H18V3.00005H19C20.11 3.00005 21 3.89005 21 5.00005V12H19V8.00005H5V19Z"/>
                </SvgIcon>
            </InputAdornment>
        }
        required
      /></div>

      let uniqueDate = <div className={classes.dateField}><TextField
      type="date"
      onChange={(evt: React.ChangeEvent<{ value: string}>) => handleFieldChange('unique', evt.target.value)}
      value={internalValue.maxStartDate !== "" ? new Date(internalValue.maxStartDate).toISOString().slice(0,10) : ""}
      startAdornment={
          <InputAdornment position="start">
          <SvgIcon className={classes.svg}>
            <path fill="currentColor" xmlns="http://www.w3.org/2000/svg" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
            </SvgIcon>
          </InputAdornment>
      }
      required
    /></div>
      

    switch(selectorVal)
    {
        case 'between-dates':
            datesBlock = <>
            {minDate}
            <div className={classes.separator} />
            {maxDate}
            </>
            break;
        case 'before':
            datesBlock = maxDate;
            break;
        case 'after':
            datesBlock = minDate;
            break;
        case 'exact-day':
            datesBlock = uniqueDate;
            break;
    }


    return <>
        <div><Selector items={selectorItems} value={selectorVal} onChange={handleSelectorChange}/></div>
        <div className={classes.datesContainer}>
            {datesBlock}
        </div>
    </>
}
